// src/pages/opendata/OpenDataAirQuality.js

import React, { useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import {
  Box,
  Grid,
  Button,
  Tooltip,
  CircularProgress,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { GoogleMap } from "@react-google-maps/api";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { REFETCH_OPTIONS } from "services/ContentManager";
import {
  useGetUgoMetricsMap,
  useGetUgoMetricsData,
} from "services/ContentOpenManager";

import { CustomHoverCard } from "./OpenDataImpact";
import { impactGoogleMapContainerStyle } from "./ImpactGoogleMap";
import { DEFAULT_LAT, DEFAULT_LNG, DEFAULT_ZOOM } from "../../constants/map";
import { InfoRedAlertBox } from "./OpenDataPage";

import {
  airMetricConfig,
  airMetricOrder,
  TAB_COLORS,
  METRIC_THRESHOLDS,
  getMetricColor,
  normalizeMetricValue,
} from "../../constants/airQuality";
import { buildDailySeries } from "../../services/airQualityDailySeries";

// Nella tab Ugo l'AQI resta "AQI Fast", come il campo dei file mensili da cui vengono i dati.
const ugoMetricConfig = {
  ...airMetricConfig,
  aqi_epa: {
    ...airMetricConfig.aqi_epa,
    titleTranslationKey: "aqiFastMetricTitle",
    descriptionTranslationKey: "aqiFastMetricDescription",
  },
};

/**
 * MAPPA - usa il geojson già arricchito (metric_avg + metric_color)
 */
function AirQualityGoogleMap({ geojson, metricLabel, unit, metricKey }) {
  const { i18n } = useTranslation();
  const [map, setMap] = useState(null);

  useEffect(() => {
    if (!map || !geojson || !Array.isArray(geojson.features)) return;

    const dataLayer = map.data;

    // Pulizia
    dataLayer.forEach((f) => dataLayer.remove(f));

    // Aggiunta geojson
    dataLayer.addGeoJson(geojson);

    // Stile poligoni
    dataLayer.setStyle((feature) => ({
      fillColor: feature.getProperty("metric_color") || "#9e9e9e",
      strokeColor: "#424242",
      strokeWeight: 0.7,
      fillOpacity: 0.6,
      strokeOpacity: 0.9,
    }));

    // InfoWindow condivisa
    let infoWindow = window.airQualityInfoWindow;
    if (!infoWindow && window.google && window.google.maps) {
      infoWindow = new window.google.maps.InfoWindow();
      window.airQualityInfoWindow = infoWindow;
    }

    // Fit bounds
    const bounds = new window.google.maps.LatLngBounds();
    dataLayer.forEach((feature) => {
      feature.getGeometry().forEachLatLng((latlng) => bounds.extend(latlng));
    });
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds);
      map.setOptions({
        restriction: {
          latLngBounds: bounds,
          strictBounds: false,
        },
      });
    }

    // Hover
    const mouseOverListener = dataLayer.addListener("mouseover", (event) => {
      dataLayer.overrideStyle(event.feature, {
        fillOpacity: 0.8,
        strokeWeight: 2,
      });
    });
    const mouseOutListener = dataLayer.addListener("mouseout", () => {
      dataLayer.revertStyle();
    });

    // Click → tooltip
    const clickListener = dataLayer.addListener("click", (event) => {
      if (!infoWindow) return;
      const name = event.feature.getProperty("name");
      const val = event.feature.getProperty("metric_avg");

      let labelVal = "N/A";
      if (typeof val === "number") {
        labelVal = val.toLocaleString(i18n.language, {
          maximumFractionDigits: 2,
        });
      }

      const content = `
        <div style="font-family: Arial, sans-serif; max-width:260px">
          <h3 style="margin:0 0 8px; font-size:16px;">${name || ""}</h3>
          <div>
            <strong>${metricLabel}:</strong> ${labelVal} ${unit || ""}
          </div>
        </div>
      `;

      infoWindow.setContent(content);
      infoWindow.setPosition(event.latLng);
      infoWindow.open(map);
    });

    // Cleanup
    return () => {
      mouseOverListener.remove();
      mouseOutListener.remove();
      clickListener.remove();
      dataLayer.forEach((f) => dataLayer.remove(f));
    };
  }, [map, geojson, metricLabel, unit, i18n.language]);

  return (
    <Box
      sx={{
        position: "relative",
        width: impactGoogleMapContainerStyle.width || "100%",
        height: impactGoogleMapContainerStyle.height || "45vh",
      }}
    >
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={{ lat: DEFAULT_LAT, lng: DEFAULT_LNG }}
        zoom={DEFAULT_ZOOM}
        onLoad={(mapInstance) => setMap(mapInstance)}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          styles: [
            { featureType: "poi", stylers: [{ visibility: "off" }] },
            { featureType: "transit", stylers: [{ visibility: "off" }] },
          ],
        }}
      />

      {/* LEGGENDA COLORI */}
      {METRIC_THRESHOLDS[metricKey] && (
        <Box
          sx={{
            position: "absolute",
            left: 8,
            bottom: 8,
            bgcolor: "rgba(255,255,255,0.9)",
            borderRadius: 1,
            boxShadow: 3,
            p: 1,
            fontSize: 12,
            maxWidth: 260,
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
          >
            {metricLabel}
          </Typography>

          {METRIC_THRESHOLDS[metricKey].map((thr, idx) => (
            <Box
              key={idx}
              sx={{ display: "flex", alignItems: "center", mb: 0.25 }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  mr: 0.75,
                  bgcolor: thr.color,
                  border: "1px solid #ccc",
                }}
              />
              <span>
                {thr.min} - {thr.max}
                {unit ? ` ${unit}` : ""}
              </span>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * LINE CHART - per la metrica selezionata
 * onDayHover(dayStr) → aggiorna la mappa su quel giorno
 * onDayOut()        → torna alla media del periodo
 */
function AirQualityLineChart({
  chartTitle,
  seriesData,
  unit,
  metricColor,
  onDayHover,
  onDayOut,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const chartHeight = isDesktop ? "40vh" : "35vh";

  const [chartData, setChartData] = useState({
    seriesData: seriesData ?? [],
  });

  useEffect(() => {
    setChartData({ seriesData });
  }, [seriesData]);

  const option = {
    title: {
      text: t(chartTitle),
      show: true,
      left: "center",
      top: 15,
    },
    colorBy: "data",
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      backgroundColor: "rgba(0,0,0,0.7)",
      textStyle: { color: "#fff" },
      transitionDuration: 0.2,
      confine: true,
      formatter: function (params) {
        const p = params[0];
        let value = p.value[1];

        value = value.toLocaleString(i18n.language, {
          maximumFractionDigits: 2,
        });

        return `
          <div>
            <strong>
              ${new Date(p.axisValue).toLocaleDateString(i18n.language)}
            </strong>
            <br/>
            <span style="color:${p.color}">●</span>${p.seriesName}&nbsp;&nbsp;
            <strong>
              ${`${value} ${unit}`}
            </strong>
          </div>
        `;
      },
    },
    grid: {
      left: "3%",
      right: "3%",
      bottom: "3%",
      containLabel: true,
      show: true,
    },
    xAxis: [
      {
        type: "time",
        axisTick: { alignWithLabel: true },
        nameTextStyle: {
          fontWeight: "bold",
          fontSize: isDesktop ? 15 : 12,
          color: "#333",
        },
        axisLabel: {
          fontSize: isDesktop ? 15 : 12,
          color: "#666",
          fontFamily: "Arial",
          rotate: 45,
          margin: 16,
          formatter: {
            year: "{MM}/{yyyy}",
            month: "{MM}/{yyyy}",
            day: "{dd}/{MM}/{yyyy}",
            hour: false,
            minute: false,
            second: false,
            millisecond: false,
          },
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        splitNumber: isDesktop ? 4 : 2,
        axisLabel: {
          fontSize: isDesktop ? 15 : 12,
          color: "#666",
          fontFamily: "Arial",
          formatter: function (value) {
            const formatted = Intl.NumberFormat(i18n.language, {
              maximumFractionDigits: 2,
            }).format(value);
            return `${formatted} ${unit}`;
          },
        },
        nameTextStyle: {
          fontWeight: "bold",
          fontSize: isDesktop ? 15 : 12,
          color: "#333",
        },
      },
    ],
    series: [
      {
        name: t(chartTitle),
        type: "line",
        data: chartData.seriesData.map((e) => [
          dayjs(e.day).valueOf(),
          e.value,
        ]),
        lineStyle: {
          color: metricColor,
          width: 2,
        },
        itemStyle: {
          color: metricColor,
        }
      },
    ],
    dataZoom: [
      {
        type: "inside",
        orient: "horizontal",
        start: 0,
        end: 100,
        filterMode: "weakFilter",
      },
    ],
  };

  // Eventi ECharts per l’hover
  const onEvents = {
    // Si scatena ogni volta che si muove l'asse verticale (tooltip trigger: 'axis')
    updateAxisPointer: (params) => {
      if (!params.axesInfo || !params.axesInfo.length) return;

      const axisInfo = params.axesInfo[0];
      if (axisInfo == null || axisInfo.value == null) return;

      const ts = axisInfo.value; // può essere timestamp o numero
      const dayStr = dayjs(ts).format("YYYY-MM-DD");

      if (onDayHover) {
        onDayHover(dayStr);
      }
    },
    // Quando il mouse esce dal grafico → torna alla media mensile
    globalout: () => {
      if (onDayOut) {
        onDayOut();
      }
    },
  };

  return (
    <ReactECharts
      option={option}
      onEvents={onEvents}
      style={{ height: chartHeight, width: "100%" }}
    />
  );
}

/**
 * ***************
 * COMPONENTE MAIN
 * ***************
 */
export function OpenDataAirQuality({ interval }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { id } = useParams();
  const { t } = useTranslation();

  const [selectedMetricKey, setSelectedMetricKey] = useState(airMetricOrder[0]);
  // giorno attualmente "hoverato" sul grafico (YYYY-MM-DD) oppure null → media periodo
  const [hoveredDay, setHoveredDay] = useState(null);

  // --- 1) mappa geojson UGO
  const { status: mapStatus, geojson: baseGeoJson } = useGetUgoMetricsMap(
    id,
    REFETCH_OPTIONS
  );

  // --- 2) dati giornalieri ugo-metrics (day, areaName, ozone_density, aqi_epa, ...)
  const params = useMemo(
    () => ({
      startDate: dayjs.utc(interval.start).startOf('month').valueOf(),
      endDate: dayjs.utc(interval.end).add(1, "day").valueOf(),
    }),
    [interval.start, interval.end]
  );

  const {
    status: dataStatus,
    data: metricsData,
  } = useGetUgoMetricsData(id, params, REFETCH_OPTIONS);

  /**
   * avgByAreaByMetric:
   * {
   *   ozone_density: { "VIII - PICONE": 123.4, ... },
   *   aqi_epa: { ... },
   *   ...
   * }
   */
  const avgByAreaByMetric = useMemo(() => {
    const accum = {};
    airMetricOrder.forEach((m) => {
      accum[m] = {}; // area -> {sum, count}
    });

    (metricsData || []).forEach((row) => {
      const area = row.areaName;
      if (!area) return;

      airMetricOrder.forEach((metricKey) => {
        const num = normalizeMetricValue(metricKey, row[metricKey]);
        if (num == null) return;

        if (!accum[metricKey][area]) {
          accum[metricKey][area] = { sum: 0, count: 0 };
        }
        accum[metricKey][area].sum += num;
        accum[metricKey][area].count += 1;
      });
    });

    const result = {};
    airMetricOrder.forEach((metricKey) => {
      const perArea = {};
      Object.entries(accum[metricKey]).forEach(([area, { sum, count }]) => {
        if (count > 0) perArea[area] = sum / count;
      });
      result[metricKey] = perArea;
    });

    return result;
  }, [metricsData]);

  /**
   * valori per giorno e area per ogni metrica:
   * valuesByMetricDayArea[metricKey][day][areaName] = value
   */
  const valuesByMetricDayArea = useMemo(() => {
    const result = {};
    airMetricOrder.forEach((m) => {
      result[m] = {};
    });

    (metricsData || []).forEach((row) => {
      const day = row.day;
      const area = row.areaName;
      if (!day || !area) return;

      airMetricOrder.forEach((metricKey) => {
        const num = normalizeMetricValue(metricKey, row[metricKey]);
        if (num == null) return;

        if (!result[metricKey][day]) {
          result[metricKey][day] = {};
        }
        result[metricKey][day][area] = num;
      });
    });

    return result;
  }, [metricsData]);

  /**
   * Serie giornaliere per ogni metrica (media su tutte le aree)
   * seriesByMetric[metricKey] = [{ day, value }], con i valori normalizzati come la mappa
   */
  const seriesByMetric = useMemo(() => buildDailySeries(metricsData), [metricsData]);

  /**
   * GeoJSON arricchito secondo la metrica selezionata
   * - se hoveredDay === null → media periodo (avgByAreaByMetric)
   * - se hoveredDay !== null → valori di quel giorno (valuesByMetricDayArea[metricKey][hoveredDay])
   */
  const enrichedGeoJson = useMemo(() => {
    if (!baseGeoJson || !Array.isArray(baseGeoJson.features)) return null;

    let perArea;
    if (
      hoveredDay &&
      valuesByMetricDayArea[selectedMetricKey] &&
      valuesByMetricDayArea[selectedMetricKey][hoveredDay]
    ) {
      // valori per quel giorno
      perArea = valuesByMetricDayArea[selectedMetricKey][hoveredDay];
    } else {
      // media periodo
      perArea =
        (avgByAreaByMetric && avgByAreaByMetric[selectedMetricKey]) || {};
    }

    const values = Object.values(perArea).filter(
      (v) => v != null && !Number.isNaN(v)
    );
    const min = values.length ? Math.min(...values) : null;
    const max = values.length ? Math.max(...values) : null;

    const cloned = {
      ...baseGeoJson,
      features: baseGeoJson.features.map((f) => ({
        ...f,
        properties: { ...(f.properties || {}) },
      })),
    };

    cloned.features.forEach((f) => {
      const props = f.properties || {};
      const areaName = props.name;
      const avgVal =
        areaName && perArea[areaName] != null ? perArea[areaName] : null;

      const color = getMetricColor(selectedMetricKey, avgVal, min, max);

      props.metric_key = selectedMetricKey;
      props.metric_avg = avgVal;
      props.metric_color = color;
      props.metric_date = hoveredDay || null;
      f.properties = props;
    });

    return cloned;
  }, [
    baseGeoJson,
    avgByAreaByMetric,
    selectedMetricKey,
    hoveredDay,
    valuesByMetricDayArea,
  ]);

  const isLoading = mapStatus === "loading" || dataStatus === "loading";
  const hasError = mapStatus === "error" || dataStatus === "error";

  const hasSomeData = airMetricOrder.some(
    (metricKey) => (seriesByMetric[metricKey] || []).length > 0
  );

  if (hasError) {
    return (
      <Box sx={{ textAlign: "center", p: 3 }}>
        {t("genericError") ||
          "Errore nel caricamento dei dati sulla qualità dell'aria."}
      </Box>
    );
  }

  if (!hasSomeData && !isLoading) {
    return (
      <Box sx={{ textAlign: "center", p: 3 }}>
        <InfoRedAlertBox textToShow={t('airQualityNoData')}></InfoRedAlertBox>
      </Box>
    );
  }

  const activeConfig = ugoMetricConfig[selectedMetricKey];
  const activeSeries = seriesByMetric[selectedMetricKey] || [];
  const activeColor =
    TAB_COLORS[airMetricOrder.indexOf(selectedMetricKey)] || TAB_COLORS[0];

  return (
    <Grid
      container
      display={"flex"}
      justifySelf={"center"}
      xs={12}
      width={isDesktop ? "75vw" : "100%"}
    >
      <CustomHoverCard sx={{ width: "100%" }}>
        <Grid container justifyContent="center">
          {/* Titolo card */}
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              my: 1.5,
              gap: 1,
            }}
          >
            <Typography variant="h4">
              {t("airQualityTitle") || "Qualità dell'aria"}
            </Typography>
          </Grid>

          {/* Bottoni metriche */}
          <Grid
            item
            xs={12}
            container
            justifyContent="center"
            sx={{ mb: 1, px: 1 }}
          >
            {airMetricOrder.map((metricKey) => {
              const cfg = ugoMetricConfig[metricKey];
              const isSelected = selectedMetricKey === metricKey;
              const color =
                TAB_COLORS[airMetricOrder.indexOf(metricKey)] ||
                TAB_COLORS[0];

              return (
                <Grid
                  item
                  key={`${metricKey}-airquality-button`}
                  xs={6}
                  sm={4}
                  md={2}
                  sx={{ mb: 1, p: 0.6 }}
                >
                  <Tooltip
                    title={t(cfg.descriptionTranslationKey) || ""}
                    placement="top"
                    arrow
                  >
                    <Button
                      sx={{
                        backgroundColor: isSelected ? color : "transparent",
                        borderColor: isSelected ? "transparent" : color,
                        color: isSelected ? "white" : color,
                        ":hover": {
                          backgroundColor: isSelected
                            ? color
                            : "rgba(0,0,0,0.08)",
                          borderColor: isSelected ? "transparent" : color,
                        },
                        height: "100%",
                        minHeight: { xs: "10vh", md: "8vh", lg: "5vh" },
                        whiteSpace: "normal",
                        lineHeight: 1.2,
                        textAlign: "center",
                        overflowWrap: "anywhere",
                      }}
                      variant={isSelected ? "contained" : "outlined"}
                      onClick={() => {
                        setSelectedMetricKey(metricKey);
                        setHoveredDay(null); // reset alla media periodo sulla nuova metrica
                      }}
                      fullWidth
                    >
                      {t(cfg.titleTranslationKey)}
                    </Button>
                  </Tooltip>
                </Grid>
              );
            })}
          </Grid>

          {/* MAPPA */}
          <Grid
            item
            xs={12}
            sx={{
              p: 1,
              minHeight: impactGoogleMapContainerStyle.height,
              display: "flex",
              alignItems: "stretch",
            }}
          >
            {isLoading && !enrichedGeoJson ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: impactGoogleMapContainerStyle.height,
                  width: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              enrichedGeoJson && (
                <AirQualityGoogleMap
                  geojson={enrichedGeoJson}
                  metricLabel={t(activeConfig.titleTranslationKey)}
                  unit={activeConfig.symbol}
                  metricKey={selectedMetricKey}
                />
              )
            )}
          </Grid>

          {/* LINE CHART */}
          <Grid
            item
            xs={12}
            sx={{
              p: 1,
              display: "flex",
              alignItems: "stretch",
            }}
          >
            {isLoading && activeSeries.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: impactGoogleMapContainerStyle.height,
                  width: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <AirQualityLineChart
                chartTitle={activeConfig.titleTranslationKey}
                seriesData={activeSeries}
                unit={activeConfig.symbol}
                metricColor={activeColor}
                onDayHover={(dayStr) => setHoveredDay(dayStr)}
                onDayOut={() => setHoveredDay(null)}
              />
            )}
          </Grid>
        </Grid>
      </CustomHoverCard>
    </Grid>
  );
}
